/**
 * Scherm 06 — Bewerkingen en berekeningen.
 *
 * Hier stelt admin of productievoorbereiding zonder code in wanneer een bewerking
 * draait en hoe zaagmaat, calibreerdikte en overmaten berekend worden. Dit is de
 * plek die op termijn de rekenregels vervangt die vroeger in code stonden — en dat is
 * al gebeurd: wat hier staat, is wat de applicatie uitrekent.
 *
 * Drie kolommen: de blokkenbibliotheek, het regelcanvas met de ALS/DAN-containers, en
 * het bewerkingpaneel. Onder de volle breedte het voorbeeldresultaat op een echt
 * onderdeel, zodat een aanpassing te controleren is vóór het opslaan.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { Actie, Bewerkingsregel, Voorwaarde, Waarde } from '@houtstaat/engine';

import {
  api,
  type BlokkenBibliotheek,
  type RegelsAntwoord,
  type RegeltestAntwoord,
} from '../api';
import { mm, useGegevens } from '../gegevens';
import { Maatlijn } from '../onderdelen/Maatlijn';
import { Bouwnummerfilter } from '../onderdelen/Bouwnummerfilter';
import { Dialoog, Schakelaar } from '../onderdelen/Dialoog';
import { IconHerhaal, IconPlus, IconZoek } from '../onderdelen/Iconen';
import {
  Actielijst,
  BLOKKLEUR,
  OPERATOREN,
  Tag,
  Voorwaardenlijst,
  waardeTekst,
} from '../onderdelen/Regelblokken';
import { tekstOp } from '../onderdelen/Routepaneel';
import { Keuzelijst } from '../onderdelen/Keuzelijst';

export function Bewerkingen() {
  const { boot, kleuren, mag, meldFout, wijzigingsteller } = useGegevens();
  const [data, setData] = useState<RegelsAntwoord | null>(null);
  const [gekozen, setGekozen] = useState<string | null>(null);
  const [concept, setConcept] = useState<Bewerkingsregel | null>(null);
  const [test, setTest] = useState<RegeltestAntwoord | null>(null);
  const [testOnderdeel, setTestOnderdeel] = useState<string | undefined>(undefined);
  const [zoek, setZoek] = useState('');
  const [bezig, setBezig] = useState(false);
  const [waardeKiezer, setWaardeKiezer] = useState<{
    huidig: Waarde;
    klaar: (nieuw: Waarde) => void;
  } | null>(null);

  const bouwnummer = boot?.bouwnummer;
  const magBewerken = mag('regelsBewerken');

  const haal = useCallback(async () => {
    if (!bouwnummer) return;
    try {
      const antwoord = await api.bewerkingsregels(bouwnummer);
      setData(antwoord);
      setGekozen((huidig) => huidig ?? antwoord.regels[0]?.id ?? null);
    } catch (e) {
      meldFout(e instanceof Error ? e.message : 'De bewerkingen konden niet geladen worden.');
    }
  }, [bouwnummer, meldFout]);

  useEffect(() => {
    void haal();
  }, [haal]);

  /*
   * Een regel kan hier half bewerkt zijn; automatisch verversen zou dat weggooien.
   * Daarom een melding zodra een collega iets wijzigt.
   */
  const [gezienTeller, setGezienTeller] = useState(wijzigingsteller);
  const nieuweWijzigingen = wijzigingsteller > gezienTeller;

  // De gekozen regel wordt in een concept gezet; opslaan gaat expliciet.
  useEffect(() => {
    const regel = data?.regels.find((r) => r.id === gekozen);
    setConcept(regel ? structuredClone(regel) : null);
  }, [data, gekozen]);

  const testen = useCallback(
    async (regels?: Bewerkingsregel[]) => {
      if (!bouwnummer) return;
      try {
        setTest(await api.regelsTesten(bouwnummer, testOnderdeel, regels));
      } catch (e) {
        meldFout(e instanceof Error ? e.message : 'De regel kon niet getest worden.');
      }
    },
    [bouwnummer, testOnderdeel, meldFout],
  );

  useEffect(() => {
    void testen();
  }, [testen]);

  const gewijzigd = useMemo(() => {
    const origineel = data?.regels.find((r) => r.id === concept?.id);
    return Boolean(concept && origineel && JSON.stringify(origineel) !== JSON.stringify(concept));
  }, [concept, data]);

  const zetConcept = (wijziging: Partial<Bewerkingsregel>) =>
    setConcept((huidig) => (huidig ? { ...huidig, ...wijziging } : huidig));

  const opslaan = async () => {
    if (!bouwnummer || !concept) return;
    setBezig(true);
    try {
      await api.bewerkingsregelOpslaan(bouwnummer, concept);
      setGezienTeller(wijzigingsteller);
      await haal();
      await testen();
    } catch (e) {
      meldFout(e instanceof Error ? e.message : 'De bewerking kon niet opgeslagen worden.');
    } finally {
      setBezig(false);
    }
  };

  if (!boot) return <div className="hs-leeg">Kies eerst een boot.</div>;
  if (!data) return <div className="hs-leeg">De bewerkingen worden geladen…</div>;

  const afdelingKleur = (afdeling: string) =>
    kleuren?.afdelingen.find((a) => a.afdeling === afdeling)?.kleur ?? 'var(--muted-licht)';

  return (
    <>
      <header className="hs-kop">
        <div className="hs-koprij">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {concept ? (
              <span
                className="hs-mono"
                style={{
                  width: 34,
                  height: 34,
                  flex: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 'var(--r-blok)',
                  background: afdelingKleur(concept.afdeling),
                  color: tekstOp(afdelingKleur(concept.afdeling)),
                  fontWeight: 600,
                  fontSize: 12,
                }}
              >
                {concept.code}
              </span>
            ) : null}
            <div>
              <p className="hs-annotatie hs-kop__gebied">Werkvoorbereiding</p>
              <h1 className="hs-h1">
                Bewerking:{' '}
                <span className="hs-mono">{concept?.naam ?? '—'}</span>
              </h1>
              <Maatlijn />
              <p className="hs-subregel">
                {data.regels.length} bewerkingen · wanneer draait hij en hoe worden de maten berekend ·{' '}
                {magBewerken ? 'een wijziging werkt meteen door in de maten' : 'alleen lezen'}
              </p>
              <Bouwnummerfilter />
            </div>
          </div>
          <div className="hs-kopacties">
            <button type="button" className="hs-knop" onClick={() => void testen(concept ? vervang(data.regels, concept) : undefined)}>
              <IconHerhaal /> Regel testen
            </button>
            {magBewerken ? (
              <>
                <button
                  type="button"
                  className="hs-knop"
                  onClick={() => {
                    const nieuw = nieuweRegel(data);
                    setData({ ...data, regels: [...data.regels, nieuw] });
                    setGekozen(nieuw.id);
                  }}
                >
                  <IconPlus /> Nieuwe bewerking
                </button>
                <button
                  type="button"
                  className="hs-knop hs-knop--primair"
                  disabled={!gewijzigd || bezig}
                  onClick={() => void opslaan()}
                >
                  {bezig ? 'Opslaan…' : 'Opslaan'}
                </button>
              </>
            ) : null}
          </div>
        </div>
      </header>

      <div className="hs-scherm">
        {/*
         * De maten volgen een regelwijziging meteen, maar de route van een kar die al
         * onderweg is niet: daar hangen de afstrepingen aan. Dat is een bewuste keuze
         * en moet dus ook te zien zijn, met de mogelijkheid het wél te doen.
         */}
        {/*
         * Dit scherm heeft werk in bewerking; automatisch verversen zou dat
         * overschrijven. Daarom alleen een melding — de gebruiker beslist zelf wanneer
         * hij ophaalt.
         */}
        {nieuweWijzigingen ? (
          <div className="hs-melding hs-melding--info">
            <span style={{ flex: 1 }}>
              Er zijn wijzigingen door iemand anders. Verversen haalt die op; wat je hier nog niet
              hebt opgeslagen gaat dan verloren.
            </span>
            <button type="button" className="hs-knop" onClick={() => void haal()}>
              Verversen
            </button>
          </div>
        ) : null}

        {data.afwijkendeKarren.length > 0 ? (
          <div className="hs-melding hs-melding--hold">
            <span style={{ flex: 1 }}>
              <b>
                {data.afwijkendeKarren.length}{' '}
                {data.afwijkendeKarren.length === 1 ? 'kar heeft' : 'karren hebben'} een route die niet
                meer klopt met deze regels.
              </b>{' '}
              De maten zijn al bijgewerkt. De route van een kar die onderweg is verandert niet
              automatisch, omdat de afstrepingen eraan hangen. Bijwerken behoudt die historie:
              afgestreepte stappen blijven staan.
            </span>
            {magBewerken ? (
              <button
                type="button"
                className="hs-knop"
                disabled={bezig}
                onClick={async () => {
                  setBezig(true);
                  try {
                    await api.routesBijwerken(boot.bouwnummer);
                    await haal();
                  } catch (e) {
                    meldFout(e instanceof Error ? e.message : 'De routes konden niet bijgewerkt worden.');
                  } finally {
                    setBezig(false);
                  }
                }}
              >
                Routes bijwerken
              </button>
            ) : null}
          </div>
        ) : null}

        <div style={{ display: 'flex', gap: 'var(--gap-paneel)', alignItems: 'stretch', minWidth: 0 }}>
          {/* ── Blokkenbibliotheek ─────────────────────────────────────────── */}
          <section className="hs-paneel" style={{ width: 220, flex: 'none' }}>
            <div className="hs-paneel__kop">
              <span className="hs-sectiekop">Blokken</span>
            </div>
            <div style={{ padding: 9 }}>
              <label className="hs-veld" style={{ marginBottom: 9 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <IconZoek maat={12} /> Zoeken
                </span>
                <input
                  className="hs-invoer"
                  value={zoek}
                  onChange={(e) => setZoek(e.target.value)}
                  placeholder="veld of tabel"
                />
              </label>

              <Blokgroep
                label="Voorwaarden"
                kleur={BLOKKLEUR.voorwaarde}
                items={data.blokken.velden
                  .filter((v) => past(v.label, zoek))
                  .map((v) => ({ sleutel: v.pad, label: v.label }))}
                uitgeschakeld={!magBewerken || !concept}
                onKies={(pad) =>
                  concept &&
                  zetConcept({
                    voorwaarde: voegVoorwaardeToe(concept.voorwaarde, {
                      soort: 'vergelijking',
                      links: { herkomst: 'veld', pad },
                      vergelijker: 'is-gevuld',
                    }),
                  })
                }
              />

              <Blokgroep
                label="Gegevens uit de coderingen"
                kleur={BLOKKLEUR.tabel}
                items={data.blokken.tabelwaarden
                  .filter((t) => past(t.label, zoek))
                  .map((t) => ({ sleutel: t.pad, label: t.label }))}
                uitgeschakeld={!magBewerken || !concept}
                onKies={(pad) =>
                  concept &&
                  zetConcept({
                    voorwaarde: voegVoorwaardeToe(concept.voorwaarde, {
                      soort: 'vergelijking',
                      links: { herkomst: 'tabel', pad },
                      vergelijker: 'is-waar',
                    }),
                  })
                }
              />

              <Blokgroep
                label="Acties"
                kleur={BLOKKLEUR.actie}
                items={[
                  { sleutel: 'activeer-bewerking', label: 'Activeer bewerking' },
                  { sleutel: 'waarde-berekenen', label: 'Waarde berekenen' },
                  { sleutel: 'parameter-instellen', label: 'Parameter instellen' },
                  { sleutel: 'label-tonen', label: 'Label in het blok' },
                  { sleutel: 'toelichting-tonen', label: 'Toelichting' },
                ].filter((a) => past(a.label, zoek))}
                uitgeschakeld={!magBewerken || !concept}
                onKies={(soort) =>
                  concept && zetConcept({ acties: [...concept.acties, nieuweActie(soort as Actie['soort'])] })
                }
              />
            </div>
          </section>

          {/* ── Regelcanvas ────────────────────────────────────────────────── */}
          <section className="hs-paneel" style={{ flex: 1, minWidth: 0 }}>
            <div className="hs-paneel__kop">
              <span className="hs-sectiekop">Regel</span>
              <span className="hs-muted">
                klik een blokje om het aan te passen · klik links een blok om het toe te voegen
              </span>
            </div>

            {concept ? (
              <div
                style={{
                  background: 'var(--rij-subtiel)',
                  minHeight: 430,
                  padding: 11,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}
              >
                <Container label="ALS" kleur={BLOKKLEUR.voorwaarde}>
                  <Voorwaardenlijst
                    voorwaarde={concept.voorwaarde}
                    blokken={data.blokken}
                    bewerkbaar={magBewerken}
                    onWijzig={(voorwaarde) => zetConcept({ voorwaarde })}
                    onKiesWaarde={(huidig, klaar) => setWaardeKiezer({ huidig, klaar })}
                  />
                  {magBewerken ? (
                    <label
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        marginTop: 8,
                        color: '#fff',
                        fontSize: 11,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={concept.voorwaarde.soort === 'of'}
                        onChange={(e) =>
                          zetConcept({
                            voorwaarde: {
                              soort: e.target.checked ? 'of' : 'en',
                              kinderen:
                                concept.voorwaarde.soort === 'en' || concept.voorwaarde.soort === 'of'
                                  ? concept.voorwaarde.kinderen
                                  : [concept.voorwaarde],
                            },
                          })
                        }
                      />
                      één voorwaarde is genoeg (OF in plaats van EN)
                    </label>
                  ) : null}
                </Container>

                <Container label="DAN" kleur={BLOKKLEUR.actie}>
                  <Actielijst
                    acties={concept.acties}
                    blokken={data.blokken}
                    bewerkbaar={magBewerken}
                    onWijzig={(acties) => zetConcept({ acties })}
                    onKiesWaarde={(huidig, klaar) => setWaardeKiezer({ huidig, klaar })}
                  />
                  {magBewerken ? (
                    <div
                      style={{
                        marginTop: 8,
                        padding: '10px 12px',
                        border: '1px dashed rgba(255,255,255,.45)',
                        borderRadius: 'var(--r-knop)',
                        color: 'rgba(255,255,255,.85)',
                        fontSize: 11.5,
                      }}
                    >
                      Kies links een blok onder <b>Acties</b> om een actie toe te voegen.
                    </div>
                  ) : null}
                </Container>
              </div>
            ) : (
              <div className="hs-leeg">Kies links een bewerking.</div>
            )}
          </section>

          {/* ── Bewerkingpaneel ────────────────────────────────────────────── */}
          <section className="hs-paneel" style={{ width: 250, flex: 'none' }}>
            <div className="hs-paneel__kop">
              <span className="hs-sectiekop">Bewerkingen</span>
            </div>
            <div style={{ maxHeight: 220, overflowY: 'auto' }}>
              {data.regels.map((regel) => (
                <button
                  key={regel.id}
                  type="button"
                  onClick={() => setGekozen(regel.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 7,
                    width: '100%',
                    padding: '6px 10px',
                    border: 0,
                    borderLeft: `3px solid ${gekozen === regel.id ? 'var(--primair)' : 'transparent'}`,
                    background: gekozen === regel.id ? 'var(--chip-zacht-bg)' : 'transparent',
                    cursor: 'pointer',
                    fontSize: 'var(--t-body)',
                    textAlign: 'left',
                    opacity: regel.actief ? 1 : 0.55,
                  }}
                >
                  <span className="hs-kleurvak" style={{ background: afdelingKleur(regel.afdeling) }} />
                  <span className="hs-mono" style={{ width: 22 }}>
                    {regel.code}
                  </span>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {regel.naam}
                  </span>
                  <span className="hs-mono hs-muted-licht" style={{ fontSize: 10 }}>
                    {regel.prioriteit}
                  </span>
                </button>
              ))}
            </div>

            {concept ? (
              <div
                style={{
                  borderTop: '1px solid var(--rand-rij)',
                  padding: 11,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 9,
                }}
              >
                <label className="hs-veld">
                  <span>Naam</span>
                  <input
                    className="hs-invoer"
                    value={concept.naam}
                    disabled={!magBewerken}
                    onChange={(e) => zetConcept({ naam: e.target.value })}
                  />
                </label>
                <div style={{ display: 'flex', gap: 7, minWidth: 0 }}>
                  <label className="hs-veld" style={{ width: 64, flex: 'none' }}>
                    <span>Code</span>
                    <input
                      className="hs-invoer hs-invoer--mono"
                      value={concept.code}
                      disabled={!magBewerken}
                      onChange={(e) => zetConcept({ code: e.target.value })}
                    />
                  </label>
                  <label className="hs-veld" style={{ flex: 1, minWidth: 0 }}>
                    <span>Volgorde</span>
                    <input
                      className="hs-invoer hs-invoer--mono"
                      type="number"
                      value={concept.prioriteit}
                      disabled={!magBewerken}
                      onChange={(e) => zetConcept({ prioriteit: Number(e.target.value) })}
                    />
                  </label>
                </div>
                <label className="hs-veld">
                  <span>Kolomnaam in de tomtom</span>
                  <input
                    className="hs-invoer hs-invoer--mono"
                    value={concept.kolomnaam}
                    disabled={!magBewerken}
                    onChange={(e) => zetConcept({ kolomnaam: e.target.value })}
                  />
                </label>
                <label className="hs-veld">
                  <span>Afdeling</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span className="hs-kleurvak" style={{ background: afdelingKleur(concept.afdeling) }} />
                    <Keuzelijst
                      className="hs-keuze"
                      style={{ flex: 1 }}
                      value={concept.afdeling}
                      disabled={!magBewerken}
                      onChange={(e) => zetConcept({ afdeling: e.target.value as Bewerkingsregel['afdeling'] })}
                    >
                      {data.afdelingen.map((a) => (
                        <option key={a} value={a}>
                          {a}
                        </option>
                      ))}
                    </Keuzelijst>
                  </span>
                </label>

                <div>
                  <span className="hs-sectielabel">Normtijd</span>
                  <div style={{ display: 'flex', gap: 7, marginTop: 4, minWidth: 0 }}>
                    <label className="hs-veld" style={{ flex: 1, minWidth: 0 }}>
                      <span>Opstarten</span>
                      <input
                        className="hs-invoer hs-invoer--mono"
                        type="number"
                        min={0}
                        step={0.5}
                        value={concept.normtijd.opstartMin}
                        disabled={!magBewerken}
                        onChange={(e) =>
                          zetConcept({
                            normtijd: { ...concept.normtijd, opstartMin: Number(e.target.value) },
                          })
                        }
                      />
                    </label>
                    <label className="hs-veld" style={{ flex: 1, minWidth: 0 }}>
                      <span>Per stuk</span>
                      <input
                        className="hs-invoer hs-invoer--mono"
                        type="number"
                        min={0}
                        step={0.1}
                        value={concept.normtijd.perStukMin}
                        disabled={!magBewerken}
                        onChange={(e) =>
                          zetConcept({
                            normtijd: { ...concept.normtijd, perStukMin: Number(e.target.value) },
                          })
                        }
                      />
                    </label>
                  </div>
                </div>

                <Schakelaar
                  label="Actief"
                  uitleg="Uit: deze bewerking draait niet meer."
                  aan={concept.actief}
                  uitgeschakeld={!magBewerken}
                  onWissel={(actief) => zetConcept({ actief })}
                />
                <Schakelaar
                  label="Foto verplicht"
                  aan={concept.fotoVerplicht}
                  uitgeschakeld={!magBewerken}
                  onWissel={(fotoVerplicht) => zetConcept({ fotoVerplicht })}
                />

                {concept.parameters.length > 0 ? (
                  <div>
                    <span className="hs-sectielabel">Parameters</span>
                    <ul style={{ margin: '4px 0 0', padding: 0, listStyle: 'none' }}>
                      {concept.parameters.map((p) => (
                        <li key={p.naam} style={{ marginBottom: 5 }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <span className="hs-mono" style={{ fontSize: 11 }}>
                              {p.naam}
                            </span>
                            <span
                              className="hs-chip"
                              style={{
                                fontSize: 9.5,
                                padding: '0 5px',
                                background: p.herkomst === 'tabel' ? '#f3e9fa' : 'var(--karwei-bg)',
                                color: p.herkomst === 'tabel' ? BLOKKLEUR.tabel : 'var(--primair)',
                                borderColor: 'transparent',
                              }}
                            >
                              {p.herkomst}
                            </span>
                          </span>
                          {p.uitleg ? (
                            <span className="hs-muted" style={{ fontSize: 10.5, lineHeight: 1.4 }}>
                              {p.uitleg}
                            </span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            ) : null}
          </section>
        </div>

        {/* ── Voorbeeldresultaat ─────────────────────────────────────────────── */}
        <Voorbeeldresultaat
          test={test}
          gekozenRegel={concept}
          onAnderOnderdeel={(id) => setTestOnderdeel(id)}
          afdelingKleur={afdelingKleur}
        />
      </div>

      {waardeKiezer ? (
        <Waardekiezer
          huidig={waardeKiezer.huidig}
          blokken={data.blokken}
          onSluiten={() => setWaardeKiezer(null)}
          onKlaar={(nieuw) => {
            waardeKiezer.klaar(nieuw);
            setWaardeKiezer(null);
          }}
        />
      ) : null}
    </>
  );
}

const past = (label: string, zoek: string): boolean =>
  label.toLowerCase().includes(zoek.trim().toLowerCase());

const vervang = (regels: Bewerkingsregel[], nieuw: Bewerkingsregel): Bewerkingsregel[] =>
  regels.map((r) => (r.id === nieuw.id ? nieuw : r));

/** De gekleurde container met de labelkolom links (ALS of DAN). */
function Container({
  label,
  kleur,
  children,
}: {
  label: string;
  kleur: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', borderRadius: 'var(--r-knop)', overflow: 'hidden', background: kleur }}>
      <div
        style={{
          width: 52,
          flex: 'none',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          padding: '10px 0',
          color: '#fff',
          fontFamily: 'var(--mono)',
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '.06em',
        }}
      >
        {label}
      </div>
      <div style={{ flex: 1, minWidth: 0, padding: '10px 11px 11px 0' }}>{children}</div>
    </div>
  );
}

function Blokgroep({
  label,
  kleur,
  items,
  uitgeschakeld,
  onKies,
}: {
  label: string;
  kleur: string;
  items: Array<{ sleutel: string; label: string }>;
  uitgeschakeld: boolean;
  onKies: (sleutel: string) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div style={{ marginBottom: 11 }}>
      <span className="hs-sectielabel">{label}</span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 4 }}>
        {items.map((item) => (
          <button
            key={item.sleutel}
            type="button"
            disabled={uitgeschakeld}
            onClick={() => onKies(item.sleutel)}
            className="hs-blokje"
            style={{ background: kleur }}
            title={uitgeschakeld ? 'Kies eerst een bewerking' : `${item.label} toevoegen`}
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Het voorbeeldresultaat: invoer, pijl, uitkomst en de uitleg in gewone taal. */
function Voorbeeldresultaat({
  test,
  gekozenRegel,
  onAnderOnderdeel,
  afdelingKleur,
}: {
  test: RegeltestAntwoord | null;
  gekozenRegel: Bewerkingsregel | null;
  onAnderOnderdeel: (id: string) => void;
  afdelingKleur: (afdeling: string) => string;
}) {
  if (!test) return null;
  const o = test.onderdeel;
  const uitkomst = test.uitkomsten.find((u) => u.regelId === gekozenRegel?.id);

  return (
    <section className="hs-paneel">
      <div className="hs-paneel__kop">
        <span className="hs-sectiekop">Voorbeeldresultaat</span>
        <span className="hs-muted">de hele regelset op één echt onderdeel</span>
        <Keuzelijst
          className="hs-keuze"
          style={{ marginLeft: 'auto', maxWidth: 320 }}
          value={o.id}
          onChange={(e) => onAnderOnderdeel(e.target.value)}
        >
          {test.keuze.map((k) => (
            <option key={k.id} value={k.id}>
              kar {k.kar} · nr {k.nummer} · {k.benaming}
            </option>
          ))}
        </Keuzelijst>
      </div>

      <div style={{ display: 'flex', gap: 'var(--gap-paneel)', padding: 12, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span className="hs-sectielabel">Invoer</span>
          <table className="hs-tabel" style={{ marginTop: 4 }}>
            <tbody>
              <tr>
                <td className="hs-muted">Onderdeel</td>
                <td>
                  nr {o.nummer} · {o.benaming}
                </td>
              </tr>
              <tr>
                <td className="hs-muted">Netto</td>
                <td className="hs-mono">
                  {o.aantal} × {mm(o.lengte)} × {mm(o.breedte)} × {mm(o.dikte)}
                </td>
              </tr>
              <tr>
                <td className="hs-muted">Materiaal</td>
                <td className="hs-mono">{o.materiaal}</td>
              </tr>
              <tr>
                <td className="hs-muted">Zijde 0 / 5</td>
                <td className="hs-mono">
                  {o.zijden.zijde0 || '—'} / {o.zijden.zijde5 || '—'}
                </td>
              </tr>
              <tr>
                <td className="hs-muted">Kanten</td>
                <td className="hs-mono">
                  {[o.zijden.zijde1, o.zijden.zijde2, o.zijden.zijde3, o.zijden.zijde4]
                    .map((z) => z || '—')
                    .join(' · ')}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          style={{
            alignSelf: 'center',
            color: 'var(--muted-licht)',
            fontSize: 22,
            padding: '0 4px',
          }}
          aria-hidden
        >
          →
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <span className="hs-sectielabel">Resultaat</span>
          <table className="hs-tabel" style={{ marginTop: 4 }}>
            <tbody>
              <tr>
                <td className="hs-muted">Zaagmaat</td>
                <td className="hs-mono" style={{ color: 'var(--primair)', fontWeight: 600 }}>
                  {mm(test.maten.bruto.lengte)} × {mm(test.maten.bruto.breedte)}
                </td>
              </tr>
              <tr>
                <td className="hs-muted">Calibreerdikte</td>
                <td className="hs-mono">
                  {test.maten.calibreerDikte != null ? `${mm(test.maten.calibreerDikte)} mm` : '—'}
                </td>
              </tr>
              {gekozenRegel ? (
                <tr>
                  <td className="hs-muted">{gekozenRegel.naam}</td>
                  <td>
                    {uitkomst?.actief ? (
                      <span style={{ color: 'var(--goed-tekst)', fontWeight: 600 }}>
                        bewerking actief JA
                      </span>
                    ) : uitkomst?.toegepast ? (
                      <span style={{ color: 'var(--primair)' }}>
                        toegepast, maar geen stap in de tomtom
                      </span>
                    ) : (
                      <span className="hs-muted">
                        draait niet: de voorwaarde klopt niet voor dit onderdeel
                      </span>
                    )}
                  </td>
                </tr>
              ) : null}
              {uitkomst?.gezet.length ? (
                <tr>
                  <td className="hs-muted">Gezet</td>
                  <td>
                    {uitkomst.gezet.map((g) => (
                      <span key={g.parameter} style={{ display: 'block' }}>
                        <span className="hs-mono">{g.parameter}</span> ={' '}
                        <span className="hs-mono" style={{ fontWeight: 600 }}>
                          {String(g.waarde)}
                        </span>
                      </span>
                    ))}
                  </td>
                </tr>
              ) : null}
              {uitkomst?.label ? (
                <tr>
                  <td className="hs-muted">In het blok</td>
                  <td className="hs-mono">{uitkomst.label}</td>
                </tr>
              ) : null}
            </tbody>
          </table>

          <span className="hs-sectielabel" style={{ display: 'block', marginTop: 9 }}>
            Route die hieruit volgt
          </span>
          <div className="hs-route" style={{ marginTop: 4 }}>
            {test.route.map((stap) => (
              <span key={stap.sleutel} className="hs-stap" style={{ flex: 1 }}>
                <span
                  className="hs-stap__kop"
                  style={{ background: afdelingKleur(stap.afdeling), color: tekstOp(afdelingKleur(stap.afdeling)) }}
                >
                  {stap.kolomnaam}
                </span>
                <span className="hs-stap__body" style={{ minHeight: 34 }}>
                  <span className="hs-stap__waarde">{stap.label}</span>
                </span>
              </span>
            ))}
          </div>
        </div>

        <div style={{ width: 380, flex: 'none' }}>
          <span className="hs-sectielabel">Wat deze bewerking doet</span>
          <p className="hs-secundair" style={{ marginTop: 4, lineHeight: 1.55 }}>
            {gekozenRegel?.uitleg ?? 'Nog geen uitleg bij deze bewerking.'}
          </p>
          {test.maten.toeslagen.length > 0 ? (
            <>
              <span className="hs-sectielabel" style={{ display: 'block', marginTop: 9 }}>
                Hoe de zaagmaat is opgebouwd
              </span>
              <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
                {test.maten.toeslagen.map((t) => (
                  <li key={t.bron} className="hs-secundair" style={{ fontSize: 11.5, lineHeight: 1.5 }}>
                    {t.toelichting}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      </div>
    </section>
  );
}

/** Eén waarde samenstellen: veld, tabelwaarde, vaste waarde, parameter of berekening. */
function Waardekiezer({
  huidig,
  blokken,
  onSluiten,
  onKlaar,
}: {
  huidig: Waarde;
  blokken: BlokkenBibliotheek;
  onSluiten: () => void;
  onKlaar: (nieuw: Waarde) => void;
}) {
  const [waarde, setWaarde] = useState<Waarde>(huidig);

  return (
    <Dialoog
      titel="Waarde kiezen"
      onSluiten={onSluiten}
      breedte={520}
      voet={
        <>
          <button type="button" className="hs-knop" onClick={onSluiten}>
            Annuleren
          </button>
          <button type="button" className="hs-knop hs-knop--primair" onClick={() => onKlaar(waarde)}>
            Gebruiken
          </button>
        </>
      }
    >
      <div className="hs-keuzeblok" style={{ flexWrap: 'wrap' }}>
        {(
          [
            ['veld', 'Gegeven van het onderdeel'],
            ['tabel', 'Uit de coderingen'],
            ['constant', 'Vaste waarde'],
            ['variabele', 'Parameter'],
            ['berekend', 'Berekening'],
          ] as Array<[Waarde['herkomst'], string]>
        ).map(([soort, label]) => (
          <button
            key={soort}
            type="button"
            className={waarde.herkomst === soort ? 'gekozen' : undefined}
            style={{ flex: '1 1 40%' }}
            onClick={() => setWaarde(leegWaarde(soort, waarde))}
          >
            {label}
          </button>
        ))}
      </div>

      {waarde.herkomst === 'veld' ? (
        <label className="hs-veld">
          <span>Veld</span>
          <Keuzelijst
            className="hs-keuze"
            value={waarde.pad}
            onChange={(e) => setWaarde({ herkomst: 'veld', pad: e.target.value })}
          >
            {blokken.velden.map((v) => (
              <option key={v.pad} value={v.pad}>
                {v.label}
              </option>
            ))}
          </Keuzelijst>
        </label>
      ) : null}

      {waarde.herkomst === 'tabel' ? (
        <label className="hs-veld">
          <span>Uit de coderingen</span>
          <Keuzelijst
            className="hs-keuze"
            value={waarde.pad}
            onChange={(e) => setWaarde({ herkomst: 'tabel', pad: e.target.value })}
          >
            {blokken.tabelwaarden.map((t) => (
              <option key={t.pad} value={t.pad}>
                {t.label}
              </option>
            ))}
          </Keuzelijst>
        </label>
      ) : null}

      {waarde.herkomst === 'constant' ? (
        <label className="hs-veld">
          <span>Vaste waarde</span>
          <input
            className="hs-invoer hs-invoer--mono"
            value={String(waarde.waarde)}
            autoFocus
            onChange={(e) => {
              const tekst = e.target.value;
              const getal = Number(tekst.replace(',', '.'));
              setWaarde({
                herkomst: 'constant',
                waarde: tekst !== '' && Number.isFinite(getal) ? getal : tekst,
              });
            }}
          />
        </label>
      ) : null}

      {waarde.herkomst === 'variabele' ? (
        <label className="hs-veld">
          <span>Parameter die een eerdere bewerking heeft gezet</span>
          <input
            className="hs-invoer hs-invoer--mono"
            value={waarde.naam}
            list="kernparameters"
            onChange={(e) => setWaarde({ herkomst: 'variabele', naam: e.target.value })}
          />
          <datalist id="kernparameters">
            {blokken.kernparameters.map((p) => (
              <option key={p.naam} value={p.naam}>
                {p.uitleg}
              </option>
            ))}
          </datalist>
        </label>
      ) : null}

      {waarde.herkomst === 'berekend' ? (
        <>
          <label className="hs-veld">
            <span>Rekenwijze</span>
            <Keuzelijst
              className="hs-keuze"
              value={waarde.operator}
              onChange={(e) =>
                setWaarde({ ...waarde, operator: e.target.value as (typeof OPERATOREN)[number]['sleutel'] })
              }
            >
              {OPERATOREN.map((o) => (
                <option key={o.sleutel} value={o.sleutel}>
                  {o.label}
                </option>
              ))}
            </Keuzelijst>
          </label>
          <div>
            <span className="hs-sectielabel">Termen</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 4 }}>
              {waarde.termen.map((term, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Tag>{waardeTekst(term, blokken)}</Tag>
                  <button
                    type="button"
                    className="hs-knop hs-knop--mini"
                    onClick={() =>
                      setWaarde({ ...waarde, termen: waarde.termen.filter((_, j) => j !== i) })
                    }
                  >
                    ✕
                  </button>
                </div>
              ))}
              <Keuzelijst
                className="hs-keuze"
                value=""
                onChange={(e) => {
                  if (!e.target.value) return;
                  const [soort, pad] = e.target.value.split('|');
                  const nieuw: Waarde =
                    soort === 'veld'
                      ? { herkomst: 'veld', pad: pad ?? '' }
                      : { herkomst: 'tabel', pad: pad ?? '' };
                  setWaarde({ ...waarde, termen: [...waarde.termen, nieuw] });
                }}
              >
                <option value="">+ term toevoegen…</option>
                <optgroup label="Gegevens van het onderdeel">
                  {blokken.velden.map((v) => (
                    <option key={v.pad} value={`veld|${v.pad}`}>
                      {v.label}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Uit de coderingen">
                  {blokken.tabelwaarden.map((t) => (
                    <option key={t.pad} value={`tabel|${t.pad}`}>
                      {t.label}
                    </option>
                  ))}
                </optgroup>
              </Keuzelijst>
              <p className="hs-muted" style={{ fontSize: 11 }}>
                Een vaste waarde of een parameter als term: voeg eerst een term toe en klik hem daarna
                aan in het canvas.
              </p>
            </div>
          </div>
        </>
      ) : null}

      {waarde.herkomst === 'keuze' ? (
        <p className="hs-muted">
          Dit blokje is een "als … dan … anders". Aanpassen van de voorwaarde erin kan nog niet in dit
          scherm; de twee uitkomsten wel, via de tags in het canvas.
        </p>
      ) : null}
    </Dialoog>
  );
}

/** Een lege waarde van de gekozen soort, met behoud van wat er al bruikbaar is. */
function leegWaarde(soort: Waarde['herkomst'], huidig: Waarde): Waarde {
  switch (soort) {
    case 'veld':
      return { herkomst: 'veld', pad: 'onderdeel.lengte' };
    case 'tabel':
      return { herkomst: 'tabel', pad: 'Fineer.Aanwezig' };
    case 'constant':
      return { herkomst: 'constant', waarde: 0 };
    case 'variabele':
      return { herkomst: 'variabele', naam: '' };
    case 'berekend':
      return { herkomst: 'berekend', operator: 'plus', termen: [huidig] };
    case 'keuze':
      return huidig;
  }
}

function nieuweActie(soort: Actie['soort']): Actie {
  switch (soort) {
    case 'activeer-bewerking':
      return { soort };
    case 'parameter-instellen':
    case 'waarde-berekenen':
      return { soort, parameter: '', waarde: { herkomst: 'constant', waarde: 0 } };
    case 'label-tonen':
    case 'toelichting-tonen':
      return { soort, patroon: '' };
  }
}

function voegVoorwaardeToe(huidig: Voorwaarde, nieuw: Voorwaarde): Voorwaarde {
  if (huidig.soort === 'en' || huidig.soort === 'of') {
    return { ...huidig, kinderen: [...huidig.kinderen, nieuw] };
  }
  return { soort: 'en', kinderen: [huidig, nieuw] };
}

/** Een nieuwe, lege bewerking achter de laatste. */
function nieuweRegel(data: RegelsAntwoord): Bewerkingsregel {
  const hoogste = data.regels.reduce((n, r) => Math.max(n, r.prioriteit), 0);
  return {
    id: `bewerking-${Date.now().toString(36)}`,
    bouwnummer: null,
    code: 'NW',
    naam: 'NIEUWE BEWERKING',
    soort: 'CNCZAAG',
    afdeling: 'Zagerij',
    kolomnaam: 'NIEUW',
    prioriteit: hoogste + 5,
    actief: false,
    normtijd: { opstartMin: 0, perStukMin: 0 },
    normZichtbaarVoorProductie: false,
    fotoVerplicht: false,
    voorwaarde: { soort: 'en', kinderen: [] },
    acties: [{ soort: 'activeer-bewerking' }],
    parameters: [],
    uitleg: '',
    gewijzigdOp: new Date().toISOString(),
  };
}
